import React, { useState } from 'react'
import { Send, Loader } from 'lucide-react'

interface ReviewFormProps {
  onSubmit: (text: string, rating: number, reviewerName: string, reviewerEmail: string, escalationEmail: string) => void
  isLoading: boolean
  error: string
}

const SAMPLE_REVIEWS = [
  {
    text: 'Amazing service! The staff was incredibly friendly and helpful. The food was delicious and came out quickly. Will definitely be back!',
    rating: 5,
    label: 'Positive Review',
  },
  {
    text: 'Terrible experience. Waited 45 minutes for cold food. The manager was rude when I complained. This is unacceptable!',
    rating: 1,
    label: 'Negative Review',
  },
  {
    text: 'The experience was okay. Food was decent but nothing special. Service was a bit slow but the staff was polite.',
    rating: 3,
    label: 'Neutral Review',
  },
  {
    text: 'Great atmosphere and excellent cocktails! The bartender really knows their stuff. A bit pricey but worth it for a special occasion.',
    rating: 4,
    label: 'Good Review',
  },
]

export default function ReviewForm({ onSubmit, isLoading, error }: ReviewFormProps) {
  const [reviewText, setReviewText] = useState('')
  const [rating, setRating] = useState(5)
  const [reviewerName, setReviewerName] = useState('')
  const [reviewerEmail, setReviewerEmail] = useState('')
  const [escalationEmail, setEscalationEmail] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (reviewText.trim()) {
      onSubmit(reviewText, rating, reviewerName.trim() || 'Anonymous', reviewerEmail.trim(), escalationEmail.trim())
    }
  }

  const loadSample = (sample: typeof SAMPLE_REVIEWS[0]) => {
    setReviewText(sample.text)
    setRating(sample.rating)
  }

  return (
    <div className="card">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Test Review</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Rating: <span className="text-lg font-bold text-primary-600">{rating}/5</span>
          </label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRating(r)}
                className={`w-12 h-12 rounded-lg font-bold transition-colors ${
                  rating === r
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* Reviewer Info */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reviewer Name
            </label>
            <input
              type="text"
              value={reviewerName}
              onChange={(e) => setReviewerName(e.target.value)}
              placeholder="e.g. Jane Smith"
              className="input"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reviewer Email
              <span className="ml-1 text-xs font-normal text-gray-500">(optional)</span>
            </label>
            <input
              type="email"
              value={reviewerEmail}
              onChange={(e) => setReviewerEmail(e.target.value)}
              placeholder="reviewer@example.com"
              className="input"
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Review Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Review Text
          </label>
          <textarea
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder="Enter a review to analyze..."
            className="textarea h-32"
            disabled={isLoading}
          />
          <p className="text-xs text-gray-600 mt-1">
            {reviewText.length} characters
          </p>
        </div>

        {/* Escalation Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Escalation Email
            <span className="ml-1 text-xs font-normal text-gray-500">
              (required for negative reviews ≤ 2★)
            </span>
          </label>
          <input
            type="email"
            value={escalationEmail}
            onChange={(e) => setEscalationEmail(e.target.value)}
            placeholder="you@example.com"
            className="input"
            disabled={isLoading}
          />
          <p className="text-xs text-gray-500 mt-1">
            An approval email will be sent here when a low-rated negative review is detected.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg">
            <p className="text-sm text-danger-700">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading || !reviewText.trim()}
          className="btn btn-primary w-full flex items-center justify-center gap-2"
        >
          {isLoading && <Loader className="w-4 h-4 animate-spin" />}
          {isLoading ? 'Processing...' : 'Analyze Review'}
          {!isLoading && <Send className="w-4 h-4" />}
        </button>
      </form>

      {/* Sample Reviews */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Sample Reviews</h3>
        <div className="space-y-2">
          {SAMPLE_REVIEWS.map((sample, idx) => (
            <button
              key={idx}
              onClick={() => loadSample(sample)}
              className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">
                  {sample.label}
                </span>
                <span className="text-xs font-bold text-primary-600">
                  {sample.rating}★
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                {sample.text}
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
